// lib/woocommerceFetchBrandTags.ts

export interface BrandTag {
    id: number;
    name: string;
    slug: string;
    productCount: number;
}

export interface BrandProductWithTags {
    id: number;
    name: string;
    slug: string;
    sku: string;
    price: string;
    stockQuantity: number;
    stockStatus: string;
    customSalePrice?: number | null;
    hasCustomDiscount?: boolean;
    image?: {
        sourceUrl: string;
        altText: string;
    };
    brands?: {
        nodes: Array<{
            id: number;
            name: string;
            slug: string;
        }>;
    };
    tags?: Array<{
        id: number;
        name: string;
        slug: string;
    }>;
}

export interface BrandProductsWithTagsResponse {
    products: BrandProductWithTags[];
    tags: BrandTag[];
    total: number;
    totalPages: number;
}

/**
 * Получение продуктов бренда с фильтрацией по меткам
 * @param brandSlug - slug бренда
 * @param page - номер страницы
 * @param perPage - количество товаров на странице
 * @param tagSlug - slug метки для фильтрации (опционально)
 * @returns Promise с данными о продуктах, метках и пагинации
 */
export async function fetchBrandProductsWithTags(
    brandSlug: string,
    page: number,
    perPage: number,
    tagSlug: string = ''
): Promise<BrandProductsWithTagsResponse> {
    const baseUrl = `https://retail.vitaline.uz/wp-json/vitaline/v1/brand-products`;
    const url = new URL(baseUrl);

    // Добавляем параметры запроса
    url.searchParams.append('brand', brandSlug);
    url.searchParams.append('page', page.toString());
    url.searchParams.append('per_page', perPage.toString());

    if (tagSlug) {
        url.searchParams.append('tag', tagSlug);
    }

    try {
        const response = await fetch(url.toString(), {
            next: {
                revalidate: 60,
                tags: [`brand-products-${brandSlug}`, `brand-tags-${brandSlug}`]
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const total = parseInt(response.headers.get('X-WP-Total') || '0', 10);
        const totalPages = parseInt(response.headers.get('X-WP-TotalPages') || '0', 10);

        return {
            products: data.products.map((p: any) => ({
                id: p.id,
                name: p.name,
                slug: p.slug,
                sku: p.sku,
                price: p.price,
                stockQuantity: p.stock_quantity,
                stockStatus: p.stock_status.toUpperCase(),
                customSalePrice: p.custom_sale_price || null,
                hasCustomDiscount: p.has_custom_discount || false,
                image: p.images?.[0]
                    ? {
                        sourceUrl: p.images[0].src,
                        altText: p.images[0].alt,
                    }
                    : undefined,
                brands: {
                    nodes: p.brands?.map((b: any) => ({
                        id: b.id,
                        name: b.name,
                        slug: b.slug,
                    })) || [],
                },
                tags: p.tags?.map((t: any) => ({
                    id: t.id,
                    name: t.name,
                    slug: t.slug,
                })) || [],
            })),
            tags: data.tags || [],
            total,
            totalPages,
        };
    } catch (error) {
        console.error('Error fetching brand products with tags:', error);
        throw new Error(`Failed to fetch brand products: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
}

/**
 * Получение только меток для конкретного бренда
 * @param brandSlug - slug бренда
 * @returns Promise с массивом меток
 */
export async function fetchBrandTags(brandSlug: string): Promise<BrandTag[]> {
    try {
        const data = await fetchBrandProductsWithTags(brandSlug, 1, 1);
        return data.tags;
    } catch (error) {
        console.error('Error fetching brand tags:', error);
        return [];
    }
}

/**
 * Получение статистики по меткам для бренда
 * @param brandSlug - slug бренда
 * @returns Promise с объектом статистики
 */
export async function fetchBrandTagsStats(brandSlug: string): Promise<{
    totalTags: number;
    totalProducts: number;
    topTags: BrandTag[];
}> {
    try {
        const data = await fetchBrandProductsWithTags(brandSlug, 1, 1);

        // Сортируем метки по количеству товаров
        const sortedTags = [...data.tags].sort((a, b) => b.productCount - a.productCount);

        return {
            totalTags: data.tags.length,
            totalProducts: data.total,
            topTags: sortedTags.slice(0, 5), // Топ 5 самых популярных меток
        };
    } catch (error) {
        console.error('Error fetching brand tags stats:', error);
        return {
            totalTags: 0,
            totalProducts: 0,
            topTags: [],
        };
    }
}